/**
 * Custom regex pattern for matching URLs with full support for:
 * - Optional protocol (http:// or https://)
 * - Domain with TLD (supports hyphens and numbers in domain segments)
 * - Optional port
 * - Optional path
 * - Optional query parameters (?key=value&key2=value2)
 * - Optional fragments (#section)
 * - Excludes email addresses (uses negative lookbehind to avoid matching domains in emails)
 */
const URL_REGEX = /(?<![@a-zA-Z0-9])(?:https?:\/\/)?(?:www\.)?(?:[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?\.)+[a-zA-Z]{2,}(?::[0-9]{1,5})?(?:\/[^\s]*)?(?:\?[^\s#]*)?(?:#[^\s]*)?\b/g;
/**
 * Extract URLs from text content using custom regex for robust URL detection
 * This ensures URLs with query parameters, fragments, and paths are preserved
 *
 * @param text - The text content to extract URLs from
 * @returns Array of normalized URLs (with https:// prefix if missing)
 */
export function extractUrls(text) {
    const matches = text.match(URL_REGEX) || [];
    const urls = matches.map(url => {
        // If the URL doesn't have a protocol, add https://
        if (!url.startsWith('http://') && !url.startsWith('https://')) {
            return `https://${url}`;
        }
        return url;
    });
    // Remove duplicates
    return [...new Set(urls)];
}
/**
 * Extract raw URLs from text content exactly as they appear
 * Used for content normalization where we need the original format
 *
 * @param text - The text content to extract URLs from
 * @returns Array of raw URLs as they appear in the text (without normalization)
 */
export function extractRawUrls(text) {
    const matches = text.match(URL_REGEX) || [];
    // Return URLs as they appear in the text (no normalization)
    return [...new Set(matches)];
}
/**
 * Get all unique links from knowledge bases
 *
 * @param knowledgeBases - Array of knowledge bases with optional links
 * @returns Array of unique URLs from all knowledge base links
 */
export function getAllLinksFromKnowledgeBases(knowledgeBases) {
    const allLinks = new Set();
    knowledgeBases.forEach(kb => {
        if (kb.links) {
            kb.links.forEach(kbl => {
                allLinks.add(kbl.link.url);
            });
        }
    });
    return Array.from(allLinks);
}
/**
 * Test if a string contains any URLs
 */
export function containsUrls(text) {
    return extractUrls(text).length > 0;
}
/**
 * Get the domain/hostname from a URL
 */
export function getDomain(url) {
    try {
        const normalizedUrl = url.startsWith('http') ? url : `https://${url}`;
        return new URL(normalizedUrl).hostname;
    }
    catch {
        return url;
    }
}
//# sourceMappingURL=url-extraction.js.map